import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import { useMemo, useState } from 'react';
import TableOptionsDropdown from '../../components/table-toolbar/table-options-dropdown.tsx';
import { useTeaContext } from '../../contexts/tea-context.tsx';
import type { EditorFormats } from '../../data/.types/editorFormats.ts';
import type { DocumentData } from '../../data/.types/tea.ts';
import { TipTapTransactionAdapter } from '../../data/TipTapTransactionEventAdapter.ts';
import type {
  CreateDocumentBody,
  MetaProperty,
} from '../../data/document/api/createDocument';
import type { TrackingEvents } from '../../data/tracking.ts';
import { DocumentEditingEventsRecorder } from '../../events/document-editing-events-recorder.ts';
import { buildExtensions } from '../../extensions';
import { clearAllPending, debounce } from '../../helpers/debounce.ts';
import { prepareHtmlWithAnchoredHeadings } from '../../helpers/prepareHtmlWithAnchoredHeadings.ts';
import { TeaBroadcaster } from '../../tea-broadcaster.ts';
import TeaEditorShortcutMessage from './tea-editor-shortcut-message.tsx';

const RemoveDefaultFocusStyle = () => (
  <style>
    {`
      .ProseMirror-focused {
        outline: none;
      }
    `}
  </style>
);

export type TeaEditorProps = {
  onError: (error: Error) => void;
  documentUuid: string;
  documentGroupId?: string;
  documentData: DocumentData;
  saveDocument: (body: CreateDocumentBody) => Promise<void>;
  meta?: MetaProperty;
  removeDefaultFocusStyle: boolean;
  editorFormats?: EditorFormats;
  recordEvent?: (event: TrackingEvents) => void;
};

const parseDocumentContent = (document: DocumentData) => {
  return document?.document ?? document?.html ?? document?.text ?? null;
};

export const TeaEditor = ({
  documentUuid,
  documentGroupId,
  documentData,
  saveDocument,
  meta,
  removeDefaultFocusStyle,
  editorFormats,
  recordEvent,
}: TeaEditorProps) => {
  const content = parseDocumentContent(documentData) ?? '<p>Hello World!</p>';
  const [broadcaster, setBroadcaster] = useState<TeaBroadcaster | null>(null);
  const { setEditor } = useTeaContext();

  const debouncedSave = useMemo(() => {
    return debounce(async (editor, broadcaster) => {
      await save(editor as Editor, broadcaster as TeaBroadcaster);
    }, TeaEditor.defaultDebounceTimeout);
  }, []);

  const editor = useEditor({
    extensions: buildExtensions(editorFormats || {}),
    content,
    editorProps: {
      attributes: {
        class: 'tea-editor',
        'aria-label': 'Tiptap editor',
        role: 'textbox',
        style: 'padding: 8px;',
      },
      transformPastedHTML: (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        const tagsToClean = doc.querySelectorAll('span, p, td, th');
        for (const tag of tagsToClean) {
          const attributesToRemove = [
            'bgColor',
            'align',
            'valign',
            'width',
            'height',
          ];

          for (const attribute of attributesToRemove) {
            if (tag.hasAttribute(attribute)) {
              tag.removeAttribute(attribute);
            }
          }
        }

        return doc.body.innerHTML;
      },
    },
    onCreate: ({ editor }) => {
      setBroadcaster(new TeaBroadcaster(documentUuid, editor));
      editor.storage.transactionEventAdapter = new TipTapTransactionAdapter(
        editor,
        recordEvent,
      );

      if (recordEvent) {
        new DocumentEditingEventsRecorder(recordEvent, editor);
      }

      setEditor(editor);
    },
    onUpdate: ({ editor }) => {
      debouncedSave(editor, broadcaster);
    },
    onDestroy: async () => {
      clearAllPending();
      if (editor !== null) {
        await save(editor, broadcaster);
      }
    },
  });

  async function save(
    editor: Editor,
    broadcaster: TeaBroadcaster | null = null,
  ) {
    const html = editorFormats?.heading?.allowAnchoredHeadings
      ? prepareHtmlWithAnchoredHeadings(editor.getHTML())
      : editor.getHTML();

    broadcaster?.publishSaving({
      html,
      document: editor.getJSON(),
    });

    await saveDocument({
      data: {
        id: documentUuid,
        type: 'document',
        attributes: {
          document: JSON.stringify(editor.getJSON()),
          html: html,
          text: editor.getText(),
          meta: meta || {},
          documentGroupId,
        },
      },
    });
    broadcaster?.publishSaved({
      html,
      document: editor.getJSON(),
    });
  }

  if (!editor) {
    return null;
  }

  return (
    <>
      {removeDefaultFocusStyle && <RemoveDefaultFocusStyle />}
      <TableOptionsDropdown editor={editor} editorFormats={editorFormats} />
      <div className="tea-editor-wrapper">
        <EditorContent editor={editor} />
      </div>
      <TeaEditorShortcutMessage editor={editor} />
    </>
  );
};

TeaEditor.defaultDebounceTimeout = 2000;
